// First-frame extraction for videos. Lives on the main thread because Workers
// don't have an HTMLVideoElement and WebCodecs' VideoDecoder doesn't cover
// every container we accept (mov, mkv, …) — `<video>` defers to the platform's
// codec set, which is exactly what we want for previews.
//
// Called once per video, after the full decrypt has materialised the bytes
// for the viewer. The output blob is then re-encrypted with K_album and
// cached alongside the image thumbs in IDB so subsequent grid renders skip
// the whole pipeline.

const VIDEO_THUMB_MAX_DIM = 512;
const VIDEO_THUMB_QUALITY = 0.85;
// Some MP4s deliver a black frame at t=0 because the first decoded sample is
// before the first sync point; nudging forward a tick gives a real frame.
const VIDEO_THUMB_SEEK_SECONDS = 0.1;

export async function generateVideoThumbnail(
  bytes: Uint8Array,
  mimeType: string,
): Promise<Blob | null> {
  if (typeof document === 'undefined') return null;
  // Copy into a fresh buffer — the caller's Uint8Array may be a view backed
  // by a transferred ArrayBuffer that we don't want the Blob to keep alive.
  const blob = new Blob([bytes.slice() as BlobPart], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.crossOrigin = 'anonymous';
  video.src = url;

  try {
    await waitForEvent(video, 'loadeddata');

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const seekTo = duration > 0 ? Math.min(VIDEO_THUMB_SEEK_SECONDS, duration / 2) : 0;
    if (seekTo > 0) {
      video.currentTime = seekTo;
      await waitForEvent(video, 'seeked');
    }

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return null;
    const scale = Math.min(1, VIDEO_THUMB_MAX_DIM / Math.max(w, h));
    const tw = Math.max(1, Math.round(w * scale));
    const th = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(video, 0, 0, tw, th);
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', VIDEO_THUMB_QUALITY);
    });
  } catch {
    return null;
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

function waitForEvent(target: HTMLVideoElement, name: 'loadeddata' | 'seeked'): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const ok = () => {
      cleanup();
      resolve();
    };
    const ko = () => {
      cleanup();
      reject(new Error(`video ${name} error`));
    };
    const cleanup = () => {
      target.removeEventListener(name, ok);
      target.removeEventListener('error', ko);
    };
    target.addEventListener(name, ok, { once: true });
    target.addEventListener('error', ko, { once: true });
  });
}
